import { ownsFeedKeys } from '@/features/action-cards-view/feed/feedShortcuts';
import { showFeedShortcuts } from '@/features/action-cards-view/feed/showFeedShortcuts';

import { steppedOpenCardId } from './next-open-card';

import type { WaitingForYouState } from './use-waiting-for-you';
import type { KeyboardEvent } from 'react';

const stepOf = (key: string): -1 | 1 | undefined =>
  key === 'ArrowDown' || key === 'j'
    ? 1
    : key === 'ArrowUp' || key === 'k'
      ? -1
      : undefined;

// Home's list answers the Feed's list keys while focus is in it: ↑ and ↓ (or K and J) move the open card, Esc closes
// it, and ? lists the keys. A field, a menu, or a control in the card that answered the key itself, such as a
// dropdown's button, keeps it
export const homeCardKeys =
  (
    home: Pick<WaitingForYouState, 'isFamilyExpanded' | 'openOnly'>,
    cardIds: readonly string[]
  ) =>
  (event: KeyboardEvent<HTMLElement>) => {
    const modified = event.metaKey || event.ctrlKey || event.altKey;
    if (modified || event.defaultPrevented || ownsFeedKeys(event.target))
      return;
    if (event.key === '?') {
      event.preventDefault();
      showFeedShortcuts();
      return;
    }
    if (event.shiftKey) return;
    // An open subtask counts as its parent's place in the list
    const openId = cardIds.find((id) => home.isFamilyExpanded(id));
    if (event.key === 'Escape') {
      if (!openId) return;
      event.preventDefault();
      home.openOnly();
      return;
    }
    const step = stepOf(event.key);
    const nextId = step && steppedOpenCardId(cardIds, openId, step);
    if (!nextId) return;
    event.preventDefault();
    home.openOnly(nextId);
    // The opened card takes focus without scrolling, so bring it into view once it is drawn
    const list = event.currentTarget;
    requestAnimationFrame(() => {
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && list.contains(focused))
        focused.scrollIntoView({ block: 'nearest' });
    });
  };
